/** Image slots (A, B, dark, flat), screen capture and RAW decoding options. */
import { useRef, useState } from 'preact/hooks';
import type { RawWhiteBalance } from '../../io/decoders/raw';
import { ACCEPT } from '../../io/formats';
import { clearSlot, loadBlob } from '../../app/actions';
import {
  averageFrames,
  captureState,
  captureSupported,
  freezeFrame,
  liveMeasure,
  resumeLive,
  startCapture,
  stopCapture,
} from '../../app/capture';
import { frames, rawOptions, SLOT_LABELS, updateSignal, type SlotId } from '../../app/store';
import { liveVideo } from '../../view/viewState';
import { Button, Field, NumberInput, Section, Select, Toggle } from '../controls';

export function openFilePicker(slot: SlotId) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = ACCEPT;
  input.onchange = () => {
    const f = input.files?.[0];
    if (f) loadBlob(slot, f, f.name);
  };
  input.click();
}

const SLOT_HELP: Record<SlotId, string> = { A: 'slot.A', B: 'slot.B', dark: 'slot.dark', flat: 'slot.flat' };

function Slot({ id }: { id: SlotId }) {
  const f = frames[id].value;
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  return (
    <div
      class={`slot${over ? ' over' : ''}${f ? ' filled' : ''}`}
      data-testid={`slot-${id}`}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setOver(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) loadBlob(id, file, file.name);
      }}
    >
      <div class="slot-head">
        <span class="slot-name" data-help={SLOT_HELP[id]}>
          {SLOT_LABELS[id]}
        </span>
        <span class="spacer" />
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          hidden
          data-testid={`file-${id}`}
          onChange={(e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) loadBlob(id, file, file.name);
            (e.target as HTMLInputElement).value = '';
          }}
        />
        <Button small icon="open" help="slot.open" onClick={() => input.current?.click()}>
          Open
        </Button>
        {f && <Button small kind="ghost" icon="close" help="slot.clear" onClick={() => clearSlot(id)} />}
      </div>
      {f ? (
        <div class="slot-body">
          <div class="slot-file" title={f.meta.name}>
            {f.meta.name}
          </div>
          <div class="slot-meta">
            {f.meta.format.toUpperCase()} · {f.width} × {f.height} · {f.isFloat ? 'float' : `${f.meta.bitDepth}-bit`} ·{' '}
            {f.channels === 1 ? 'grey' : 'RGB'}
          </div>
          <div class="slot-enc" data-help="slot.encoding">
            {f.meta.encoding.description}
          </div>
          {(Object.keys(f.meta.info).length > 0 || f.meta.warnings.length > 0) && (
            <details class="slot-details">
              <summary>Details</summary>
              <table class="kv">
                <tbody>
                  {Object.entries(f.meta.info).map(([k, v]) => (
                    <tr key={k}>
                      <th>{k}</th>
                      <td>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {f.meta.warnings.map((w) => (
                <div class="hint warn" key={w}>
                  {w}
                </div>
              ))}
            </details>
          )}
        </div>
      ) : (
        <div class="slot-empty">Drop a file here</div>
      )}
    </div>
  );
}

export function SourcesPanel() {
  const cap = captureState.value;
  const ro = rawOptions.value;
  return (
    <div class="sources-panel">
      <Section title="Screen capture" help="capture.section">
        {!captureSupported() ? (
          <div class="hint warn">Screen capture needs getDisplayMedia (Chrome, Edge, Firefox desktop).</div>
        ) : !cap.active ? (
          <Button icon="capture" kind="primary" help="capture.start" onClick={() => startCapture()} testId="capture-start">
            Start capture…
          </Button>
        ) : (
          <div class="capture-live">
            <div class="muted small">
              {cap.info?.label || cap.info?.surface} · {cap.info?.width} × {cap.info?.height}
            </div>
            <div class="inline">
              <Button
                icon="snapshot"
                kind="primary"
                help="capture.freeze"
                onClick={() => freezeFrame('A')}
                testId="capture-freeze"
                disabled={cap.grabbing > 0}
              >
                {cap.grabbing ? `Grabbing ${cap.grabbing}/${averageFrames.value}` : 'Freeze → A'}
              </Button>
              <Button small help="capture.freezeB" onClick={() => freezeFrame('B')} disabled={cap.grabbing > 0}>
                → B
              </Button>
              {!liveVideo.value && (
                <Button small help="capture.resume" onClick={resumeLive}>
                  Live
                </Button>
              )}
              <Button small kind="danger" icon="stop" help="capture.stop" onClick={stopCapture} testId="capture-stop">
                Stop
              </Button>
            </div>
            <Toggle
              label="Live measurements"
              checked={liveMeasure.value}
              help="capture.liveMeasure"
              testId="live-measure"
              onChange={(v) => (liveMeasure.value = v)}
            />
            <Field label="Average frames" help="capture.average">
              <NumberInput value={averageFrames.value} min={1} max={256} integer onChange={(v) => (averageFrames.value = v)} />
            </Field>
          </div>
        )}
      </Section>
      {(['A', 'B', 'dark', 'flat'] as SlotId[]).map((id) => (
        <Slot key={id} id={id} />
      ))}
      <Section title="Camera RAW decoding" defaultOpen={false} help="raw.section">
        <Field label="White balance" help="raw.wb">
          <Select<RawWhiteBalance>
            value={ro.whiteBalance}
            options={[
              { value: 'camera', label: 'As shot (camera)' },
              { value: 'auto', label: 'Automatic (grey world)' },
              { value: 'unity', label: 'None (unity multipliers)' },
            ]}
            onChange={(v) => updateSignal(rawOptions, { whiteBalance: v })}
          />
        </Field>
        <Field label="Demosaic" help="raw.quality">
          <Select<number>
            value={ro.quality}
            options={[
              { value: 0, label: 'Bilinear (fast)' },
              { value: 1, label: 'VNG' },
              { value: 2, label: 'PPG' },
              { value: 3, label: 'AHD (default)' },
              { value: 4, label: 'DCB' },
            ]}
            onChange={(v) => updateSignal(rawOptions, { quality: v })}
          />
        </Field>
        <Toggle
          label="Half size (2×2 binning, no demosaic)"
          checked={ro.halfSize}
          help="raw.half"
          onChange={(v) => updateSignal(rawOptions, { halfSize: v })}
        />
        <div class="muted small">Applies to RAW files opened after the change.</div>
      </Section>
    </div>
  );
}
